import { RadioGroup } from '@headlessui/react';
import cn from 'classnames';
import React, { ReactNode, useMemo, useRef } from 'react';
import { animated } from '@react-spring/web';
import { config } from '@react-spring/web';
import { useSpring } from '@react-spring/web';
import useMeasure from 'react-use-measure';

interface OptionChildFnProps {
  checked: boolean;
  active: boolean;
  disabled: boolean;
}

export interface ValueProps {
  tabValue: string;
  automationTag?: string;
}

type TabsRadioGroupProps<V extends string | number> = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'onChange'
> &
  Omit<React.ComponentProps<typeof RadioGroup>, 'value'> & {
    currValue: V extends infer C ? C : never;
    values: ValueProps[];
    renderTab: (
      props: OptionChildFnProps & { value: ValueProps; key: number },
    ) => ReactNode;
    minWidth?: string;
    gap?: 'sm' | 'md' | 'lg';
    underlineBgClass?: string;
  };

export const TabsRadioGroup = <V extends string | number>({
  currValue,
  values,
  renderTab,
  onChange,
  minWidth,
  gap,
  underlineBgClass,
  ...restProps
}: TabsRadioGroupProps<V>) => {
  const [measureRef, measurements] = useMeasure();
  const radioGroupRef = useRef<HTMLDivElement>(null);

  const { currWidth, currLeft } = useMemo(() => {
    if (!radioGroupRef.current) {
      return {
        currWidth: 0,
        currLeft: 0,
      };
    }

    const selectedEl = radioGroupRef.current.querySelector(
      '[data-tab-value="' + currValue + '"] > *:first-child',
    ) as HTMLElement;
    if (!selectedEl) {
      return {
        currWidth: 0,
        currLeft: 0,
      };
    }

    return {
      currWidth: selectedEl.getBoundingClientRect().width,
      currLeft: selectedEl.offsetLeft,
    };
    // We are about to disable this rule because we need this logic to re-trigger any time the measurements change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioGroupRef, measurements, currValue]);

  const { width: tablineWidth, left: tablineLeft } = useSpring({
    width: currWidth,
    left: currLeft,
    config: config.default,
  });

  return (
    <div ref={measureRef} className="relative w-full">
      <RadioGroup
        ref={radioGroupRef}
        value={currValue}
        className="flex w-full border-b-1 border-gray-400 mx-auto relative"
        onChange={(v) => {
          onChange && onChange(v);
        }}
        {...restProps}
      >
        {values.map((value, key) => {
          return (
            <RadioGroup.Option
              key={value.tabValue}
              value={value.tabValue}
              className="flex items-center content-center"
              style={{ minWidth: minWidth ?? '15rem' }}
              data-tab-value={value.tabValue}
              data-qa={value.automationTag ? value.automationTag : undefined}
            >
              {(props) => {
                return (
                  <div
                    className={cn(
                      'items-center text-lg font-sans py-4 w-full',
                      {
                        'text-secondary': props.checked,
                        'cursor-pointer': !props.checked,
                        'mr-12': !gap || gap === 'lg',
                        'mr-8': gap === 'md',
                        'mr-4': gap === 'sm',
                      },
                    )}
                  >
                    {renderTab({ ...props, value, key })}
                  </div>
                );
              }}
            </RadioGroup.Option>
          );
        })}
        <animated.div
          className={cn(
            'absolute -bottom-0.5 h-[2px] rounded-sm',
            underlineBgClass ?? 'bg-secondary',
          )}
          style={{
            width: tablineWidth,
            left: tablineLeft,
          }}
        />
      </RadioGroup>
    </div>
  );
};
